/**
 * 收藏筛选组件
 *
 * 功能：
 * 1. Items页面显示分类选项卡（全部、网页、图片等）
 * 2. 所有页面显示排序选择器
 * 3. 处理URL参数变化
 */

import {
  ArrowUp,
  AppWindow,
  ImageIcon,
  FileText,
  FileType,
  SquarePlay,
  LayoutGrid,
  LayoutList,
  Menu,
  CalendarArrowDown,
  CalendarArrowUp,
  Package2,
  Star,
  Search,
  Shapes,
  CopyMinus,
} from "lucide-react";
import { FolderIcon, FolderKey, FolderLock, Tags as TagIcon } from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams, useParams, useLocation, Link } from "react-router";

import {
  useItemActions,
  useLayoutMode,
  useSidebarData,
  useSortBy,
  useSortOrder,
} from "@/modules/mine";
import { usePrivacySession } from "@/modules/mine/privacy/use-privacy-session";
import type { LayoutMode } from "@/modules/mine/types";
import type { FolderData as SidebarFolderData } from "@/modules/mine/types";
import {
  ROUTE_PATHS,
  buildMineFolderPath,
  buildMineTagPath,
} from "@/shared/constants/route-paths";
import { setDocumentTitle } from "@/shared/lib/document-title";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
} from "@/shared/ui/breadcrumb";
import { Button } from "@/shared/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu";
import { Separator } from "@/shared/ui/separator";






// 分类选项配置
const CATEGORY_OPTIONS = [
  { value: "all", label: "全部", icon: Shapes },
  { value: "website", label: "网页", icon: AppWindow },
  { value: "image", label: "图片", icon: ImageIcon },
  { value: "text", label: "笔记", icon: FileText },
  { value: "pdf", label: "PDF", icon: FileType },
  { value: "video", label: "视频", icon: SquarePlay },
];
const DUPLICATE_CATEGORY_OPTIONS = CATEGORY_OPTIONS.filter(({ value }) =>
  value === "all" || value === "website" || value === "image" || value === "text"
);

// 布局选项配置
const LAYOUT_OPTIONS = [
  { value: "card", label: "卡片模式", icon: LayoutGrid },
  { value: "list", label: "列表模式", icon: LayoutList },
  { value: "headline", label: "极简模式", icon: Menu },
  // { value: "waterfall", label: "瀑布模式", icon: LayoutDashboard },
];

// 排序选项配置
const SORT_OPTIONS = [
  {
    value: "id",
    label: "创建时间",
    iconDown: CalendarArrowDown,
    iconUp: CalendarArrowUp,
  },
];

// 面包屑项类型
interface BreadcrumbItemData {
  label: string;
  path: string;
  icon?: React.ElementType;
}

export function ItemsFilter() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();

  // 从 store 获取状态
  const layoutMode = useLayoutMode();
  const sortBy = useSortBy();
  const sortOrder = useSortOrder();
  const { setLayoutMode, setSortBy, toggleSortOrder, closePreview } = useItemActions();

  // 从 Context 获取共享数据
  const { folders: foldersData, tags: tagsData } = useSidebarData();
  const { isUnlocked } = usePrivacySession();

  // 从 URL 获取当前分类（默认为'all'）
  const categoryOptions = location.pathname === ROUTE_PATHS.MINE_DUPLICATES
    ? DUPLICATE_CATEGORY_OPTIONS
    : CATEGORY_OPTIONS;
  const requestedFormat = searchParams.get("format") || "all";
  const format = categoryOptions.some((option) => option.value === requestedFormat)
    ? requestedFormat
    : "all";
  const searchQuery = searchParams.get("q")?.trim();

  // 面包屑数据
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItemData[]>([]);

  // 递归查找文件夹路径
  const findFolderPath = useCallback((
    folders: SidebarFolderData[],
    targetId: string,
    path: BreadcrumbItemData[] = [],
    parentPrivate = false,
  ): BreadcrumbItemData[] | null => {
    for (const folder of folders) {
      const isPrivate = parentPrivate || folder.privacy_enabled;
      const icon = isPrivate ? (isUnlocked ? FolderKey : FolderLock) : FolderIcon;
      const currentPath = [...path, { label: folder.name, path: buildMineFolderPath(folder.id), icon }];
      
      if (String(folder.id) === targetId) {
        return currentPath;
      }
      
      if (folder.sub_folders && folder.sub_folders.length > 0) {
        const foundPath = findFolderPath(folder.sub_folders, targetId, currentPath, isPrivate);
        if (foundPath) return foundPath;
      }
    }
    return null;
  }, [isUnlocked]);

  // 加载面包屑数据
  useEffect(() => {
    const loadBreadcrumbs = () => {
      // 如果是未分类页面 (/mine)
      if (location.pathname === ROUTE_PATHS.MINE) {
        setBreadcrumbs([{ label: "未分类", path: ROUTE_PATHS.MINE, icon: Package2 }]);
        return;
      }

      // 如果是全部收藏页面 (/mine/all)
      if (location.pathname === ROUTE_PATHS.MINE_ALL) {
        const formatLabel = CATEGORY_OPTIONS.find((opt) => opt.value === format)?.label;
        const formatIcon = CATEGORY_OPTIONS.find((opt) => opt.value === format)?.icon;
        if (format === "all" || !format) {
          setBreadcrumbs([{ label: "全部收藏", path: ROUTE_PATHS.MINE_ALL, icon: Star }]);
        } else {
          setBreadcrumbs([
            { label: "全部收藏", path: ROUTE_PATHS.MINE_ALL, icon: Star },
            { label: formatLabel || format, path: `${ROUTE_PATHS.MINE_ALL}?format=${format}`, icon: formatIcon },
          ]);
        }
        return;
      }

      if (location.pathname === ROUTE_PATHS.MINE_DUPLICATES) {
        setBreadcrumbs([{ label: "查找重复收藏", path: ROUTE_PATHS.MINE_DUPLICATES, icon: CopyMinus }]);
        return;
      }

      if (location.pathname === ROUTE_PATHS.MINE_SEARCH) {
        setBreadcrumbs([{
          label: searchQuery ? `搜索 “${searchQuery}”` : "搜索结果",
          path: `${ROUTE_PATHS.MINE_SEARCH}${location.search}`,
          icon: Search,
        }]);
        return;
      }

      // 如果是文件夹页面
      if (params.folderId) {
        const path = findFolderPath(foldersData, params.folderId);
        
        if (path) {
          setBreadcrumbs(path);
        } else {
          setBreadcrumbs([{ label: "未知文件夹", path: location.pathname }]);
        }
        return;
      }

      // 如果是标签页面
      if (params.tagId) {
        const tag = tagsData.find((t) => String(t.id) === params.tagId);
        
        if (tag) {
          setBreadcrumbs([
            { label: tag.name, path: buildMineTagPath(tag.id), icon: TagIcon },
          ]);
        } else {
          setBreadcrumbs([{ label: "未知标签", path: location.pathname }]);
        }
        return;
      }

      // 默认情况
      setBreadcrumbs([]);
    };

    loadBreadcrumbs();
  }, [location.pathname, location.search, params.folderId, params.tagId, format, searchQuery, findFolderPath, foldersData, tagsData]);

  useEffect(() => {
    const current = breadcrumbs[breadcrumbs.length - 1];
    if (params.folderId && current?.icon && current.path === buildMineFolderPath(params.folderId)) {
      setDocumentTitle(current.icon === FolderLock
        ? "文件夹 - Pinbox"
        : `${current.label} · 文件夹 - Pinbox`);
    } else if (params.tagId && current?.icon && current.path === buildMineTagPath(params.tagId)) {
      setDocumentTitle(`${current.label} · 标签 - Pinbox`);
    }
  }, [breadcrumbs, params.folderId, params.tagId]);

  /**
   * 处理分类切换
   * 更新URL参数，触发页面重新渲染和数据重新加载
   */
  const handleCategoryChange = (value: string) => {
    const newParams = new URLSearchParams(searchParams);
    
    if (value === "all") {
      // "全部"分类：移除category参数，保持URL简洁
      newParams.delete("format");
    } else {
      // 其他分类：设置format参数
      newParams.set("format", value);
    }
    
    // 更新URL（会触发数据重新加载）
    navigate(`?${newParams.toString()}`, { replace: true });
  };

  return (
    <div data-slot="items-filter" className="flex justify-between items-center pt-2 pb-4">
      <Breadcrumb>
        <BreadcrumbList>
          {breadcrumbs.map((crumb, index) => {
            const Icon = crumb.icon;
            const isLast = index === breadcrumbs.length - 1;

            return (
              <div key={crumb.path} className="flex items-center">
                <BreadcrumbItem className="hidden md:block">
                  {isLast ? (
                    <BreadcrumbPage data-slot="items-filter-title" className="flex items-center gap-1.5 text-2xl font-black tracking-tight">
                      {Icon && <Icon className="h-6 w-6" />}
                      {crumb.label}
                    </BreadcrumbPage>
                  ) : (
                    <BreadcrumbLink asChild>
                      <Link to={crumb.path} data-slot="items-filter-title" className="flex items-center gap-1.5 text-2xl font-black tracking-tight">
                        {Icon && <Icon className="h-6 w-6" />}
                        {crumb.label}
                      </Link>
                    </BreadcrumbLink>
                  )}
                </BreadcrumbItem>
                {!isLast && <BreadcrumbSeparator className="hidden md:block" />}
              </div>
            );
          })}
        </BreadcrumbList>
      </Breadcrumb>
      <div className="flex items-center gap-2">
        {/* 1. 分类筛选菜单 */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="font-normal">
              {(() => {
                const Icon = categoryOptions.find(
                  (opt) => opt.value === format
                )?.icon;
                return Icon ? <Icon className="w-4 h-4" /> : null;
              })()}
              {categoryOptions.find((opt) => opt.value === format)?.label}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            {categoryOptions.map((option) => {
              const Icon = option.icon;
              return (
                <DropdownMenuItem
                  key={option.value}
                  onClick={() => handleCategoryChange(option.value)}
                  className={format === option.value ? "bg-accent cursor-pointer" : "cursor-pointer"}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {option.label}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* 2. 布局模式切换菜单 */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="font-normal">
              {(() => {
                const Icon = LAYOUT_OPTIONS.find(
                  (opt) => opt.value === layoutMode
                )?.icon;
                return Icon ? <Icon className="w-4 h-4" /> : null;
              })()}
              {LAYOUT_OPTIONS.find((opt) => opt.value === layoutMode)?.label}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            {LAYOUT_OPTIONS.map((option) => {
              const Icon = option.icon;
              return (
                <DropdownMenuItem
                  key={option.value}
                  onClick={() => {
                    setLayoutMode(option.value as LayoutMode);
                    if (option.value === 'card') {
                      closePreview();
                    }
                  }}
                  className={layoutMode === option.value ? "bg-accent cursor-pointer" : "cursor-pointer"}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {option.label}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* 3. 排序方式和排序方向组合 - ButtonGroup */}
        <div className="inline-flex items-center">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="rounded-none rounded-l-md font-normal hover:bg-accent"
              >
                {(() => {
                  const Icon =
                    sortOrder === "asc"
                      ? SORT_OPTIONS.find((opt) => opt.value === sortBy)?.iconUp
                      : SORT_OPTIONS.find((opt) => opt.value === sortBy)
                          ?.iconDown;
                  return Icon ? <Icon className="w-4 h-4" /> : null;
                })()}
                {SORT_OPTIONS.find((opt) => opt.value === sortBy)?.label}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {SORT_OPTIONS.map((option) => {
                const Icon =
                  sortOrder === "asc" ? option.iconUp : option.iconDown;
                return (
                  <DropdownMenuItem
                    key={option.value}
                    onClick={() => setSortBy(option.value as "id" | "views")}
                    className={sortBy === option.value ? "bg-accent cursor-pointer" : "cursor-pointer"}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {option.label}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* 升序/降序切换按钮 - 带旋转动画 */}
          <Separator orientation="vertical" className="h-4!" />
          <Button
            variant="ghost"
            size="sm"
            className="rounded-none rounded-r-md font-normal"
            onClick={toggleSortOrder}
            title={sortOrder === "asc" ? "升序" : "降序"}
          >
            <ArrowUp
              className={`w-4 h-4 transition-transform duration-300 ease-in-out ${
                sortOrder === "desc" ? "rotate-180" : "rotate-0"
              }`}
            />
          </Button>
        </div>
      </div>
    </div>
  );
}
